import React, { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import Sidebar from 'apollo-react/components/Sidebar';

const SidebarCustom = (props) => {
  const location = useLocation();
  const sidebarRef = useRef(null);

  // Expand parent menu items when a child is active
  useEffect(() => {
    // Skip if sidebar is collapsed (items are not visible anyway)
    if (props.isSidebarCollapsed) {
      return;
    }

    // Find all menu items with children
    const menuItemsWithChildren = props.menuItems.filter(item => item.menuItems && item.menuItems.length > 0);
    
    // Find which parents have active children
    const parentsToExpand = menuItemsWithChildren.filter(parentItem => {
      return parentItem.menuItems.some(childItem => {
        if (!childItem.pathname) return false;
        return location.pathname.startsWith(childItem.pathname);
      });
    });

    // Expand all parents that have active children
    if (parentsToExpand.length > 0) {
      // Try multiple times with increasing delays to ensure DOM is ready
      const tryExpand = (attempt = 0) => {
        if (attempt > 10) return; // Max 10 attempts
        
        // Try to find the sidebar element - it might be in the ref or in the document
        const sidebarElement = sidebarRef.current?.querySelector('[class*="MuiDrawer-root"]') ||
                              sidebarRef.current?.querySelector('[class*="MuiPaper-root"]') ||
                              document.querySelector('[class*="MuiDrawer-root"][class*="MuiDrawer-persistent"]') ||
                              sidebarRef.current;
        
        if (sidebarElement) {
          // Find all buttons in the sidebar
          const allButtons = sidebarElement.querySelectorAll('button[class*="MuiButton-root"]');
          
          let foundAndExpanded = 0;
          
          // For each parent that needs to be expanded
          parentsToExpand.forEach((parentItem) => {
            // Find the button that matches this parent
            Array.from(allButtons).forEach((button) => {
              const buttonText = button.textContent?.trim().replace(/\s+/g, ' ');
              const parentText = parentItem.text?.trim().replace(/\s+/g, ' ');
              
              // Match by text (normalize whitespace)
              if (buttonText === parentText || buttonText.includes(parentText) || parentText.includes(buttonText)) {
                // Verify it's expandable (has multiple SVG icons - icon + chevron)
                const svgIcons = button.querySelectorAll('svg');
                if (svgIcons.length > 1) {
                  // Check if it's already expanded
                  // Look for the expandedList class on the button
                  const hasExpandedClass = Array.from(button.classList).some(cls => 
                    cls.includes('expandedList') || cls.includes('expanded')
                  );
                  
                  // Also check if there's a visible nested list (MenuList) next to it
                  let currentElement = button.parentElement;
                  let isListVisible = false;
                  
                  // Check siblings for the nested list
                  for (let i = 0; i < 5 && currentElement; i++) {
                    let nextSibling = currentElement.nextElementSibling;
                    for (let j = 0; j < 3 && nextSibling; j++) {
                      const classList = Array.from(nextSibling.classList || []);
                      if (nextSibling.tagName === 'UL' || 
                          classList.some(cls => cls.includes('nestedList') || cls.includes('MenuList'))) {
                        const isExpanded = nextSibling.offsetHeight > 0 && 
                                          nextSibling.style.display !== 'none' &&
                                          !classList.some(cls => cls.includes('collapsed'));
                        if (isExpanded) {
                          isListVisible = true;
                          break;
                        }
                      }
                      nextSibling = nextSibling.nextElementSibling;
                    }
                    if (isListVisible) break;
                    currentElement = currentElement.parentElement;
                    if (!currentElement || currentElement === sidebarElement) break;
                  }
                  
                  const isExpanded = hasExpandedClass || isListVisible;
                  
                  if (!isExpanded) {
                    // Click to expand
                    button.click();
                    foundAndExpanded++;
                  } else {
                    foundAndExpanded++;
                  }
                }
              }
            });
          });
          
          // If we didn't find all parents, try again after a delay
          if (foundAndExpanded < parentsToExpand.length && attempt < 10) {
            setTimeout(() => tryExpand(attempt + 1), 150 * (attempt + 1));
          }
        } else if (attempt < 10) {
          // Sidebar element not ready, try again
          setTimeout(() => tryExpand(attempt + 1), 150 * (attempt + 1));
        }
      };
      
      // Start trying after initial delay
      const expandTimer = setTimeout(() => tryExpand(0), 200);
      
      return () => clearTimeout(expandTimer);
    }
  }, [location.pathname, props.menuItems, props.isSidebarCollapsed]);

  // Helper function to expand a parent menu item by its text
  const expandParentMenuItem = (parentText) => {
    if (props.isSidebarCollapsed) return;
    
    const tryExpand = (attempt = 0) => {
      if (attempt > 5) return;
      
      const sidebarElement = sidebarRef.current?.querySelector('[class*="MuiDrawer-root"]') ||
                            sidebarRef.current?.querySelector('[class*="MuiPaper-root"]') ||
                            document.querySelector('[class*="MuiDrawer-root"][class*="MuiDrawer-persistent"]') ||
                            sidebarRef.current;
      
      if (sidebarElement) {
        const allButtons = sidebarElement.querySelectorAll('button[class*="MuiButton-root"]');
        
        const normalizedParentText = parentText?.trim().replace(/\s+/g, ' ');
        
        Array.from(allButtons).forEach((button) => {
          const buttonText = button.textContent?.trim().replace(/\s+/g, ' ');
          if (buttonText === normalizedParentText || buttonText.includes(normalizedParentText) || normalizedParentText.includes(buttonText)) {
            const svgIcons = button.querySelectorAll('svg');
            if (svgIcons.length > 1) {
              const hasExpandedClass = Array.from(button.classList).some(cls => 
                cls.includes('expandedList') || cls.includes('expanded')
              );
              
              let currentElement = button.parentElement;
              let isListVisible = false;
              
              for (let i = 0; i < 5 && currentElement; i++) {
                let nextSibling = currentElement.nextElementSibling;
                for (let j = 0; j < 3 && nextSibling; j++) {
                  const classList = Array.from(nextSibling.classList || []);
                  if (nextSibling.tagName === 'UL' || 
                      classList.some(cls => cls.includes('nestedList') || cls.includes('MenuList'))) {
                    const isExpanded = nextSibling.offsetHeight > 0 && 
                                      nextSibling.style.display !== 'none' &&
                                      !classList.some(cls => cls.includes('collapsed'));
                    if (isExpanded) {
                      isListVisible = true;
                      break;
                    }
                  }
                  nextSibling = nextSibling.nextElementSibling;
                }
                if (isListVisible) break;
                currentElement = currentElement.parentElement;
                if (!currentElement || currentElement === sidebarElement) break;
              }
              
              const isExpanded = hasExpandedClass || isListVisible;
              
              if (!isExpanded) {
                button.click();
              }
            }
          }
        });
      } else if (attempt < 5) {
        setTimeout(() => tryExpand(attempt + 1), 100 * (attempt + 1));
      }
    };
    
    setTimeout(() => tryExpand(0), 100);
  };

  // Process menu items
  let menuItemsUpdated = props.menuItems.map((item, index) => {
    const updatedItem = { ...item };
    if(item.handleOnClick) {
      updatedItem.onClick = () => {
        item.handleOnClick();
      }
    }

    if(item.menuItems) {
      updatedItem.menuItems = item.menuItems.map((innerItem, innerIndex) => {
        const innerItemCopy = { ...innerItem };
        if(innerItem.handleOnClick) {
          innerItemCopy.onClick = () => {
            // Expand parent when child is clicked
            if (innerItem.parentName) {
              expandParentMenuItem(innerItem.parentName);
            } else if (item.text) {
              expandParentMenuItem(item.text);
            }
            innerItem.handleOnClick();
          }
        }
        return innerItemCopy;
      })
    }
    delete updatedItem.handleOnClick;
    return { ...updatedItem };
  });

  menuItemsUpdated = menuItemsUpdated.map((item, index) => {
    let innerMenuItemSelected = false;
    if (item.menuItems) {
      item.menuItems = item.menuItems.map((innerItem) => {
        if (
          innerItem.pathname &&
          location.pathname.startsWith(innerItem.pathname)
        ) {
          innerMenuItemSelected = true;
          return { ...innerItem, active: true };
        } else {
          return { ...innerItem };
        }
      });
      return {
        ...item,
        menuItems: [...item.menuItems],
        active: innerMenuItemSelected ? true : false,
      };
    } else if (
      item.pathname &&
      location.pathname.startsWith(item.pathname)
    ) {
      return { ...item, active: true };
    } else {
      return { ...item };
    }
  });

    return (
        <div ref={sidebarRef} style={{ position: 'relative' }}>
          <Sidebar
            sx={{ '& .MuiPaper-root': { width: '320px', background: "linear-gradient(#250056, #0076AE)" } }}
            logoComponentProps={{
              orgName: 'IQVIA',
              appName: 'Workflow'
            }}
            // {...props}
            menuItems = {menuItemsUpdated}
            collapsed = {props.isSidebarCollapsed}
            onToggleCollapsed={() => {
              props.setIsSidebarCollapsed(!props.isSidebarCollapsed);
            }}
          />
        </div>
    );
};

export default SidebarCustom;